import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api, CONTACT } from "@/lib/api";
import Navbar from "@/components/noir/Navbar";
import Footer from "@/components/noir/Footer";
import { ArrowLeft, Check, MessageCircle } from "lucide-react";

function fmt12(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

export default function Confirmation() {
  const { reference } = useParams();
  const location = useLocation();
  const ownerWa = location.state?.owner_notify_url;
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/appointments/reference/${reference}`).then((r) => setAppt(r.data)).catch(() => setError("Not found"));
  }, [reference]);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-[#F7F3EC]">
      <Navbar />
      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border border-[#B8935A]">
            <Check size={30} className="text-[#B8935A]" strokeWidth={1.5} />
          </div>
          <p className="eyebrow mt-8" data-testid="confirmation-eyebrow">Appointment Request Received</p>
          <h1 className="serif text-4xl md:text-6xl mt-4 leading-tight">
            Thank you for choosing <span className="serif-italic text-[#B8935A]">NOIR NAILS</span>
          </h1>
          <p className="mt-6 text-[#A1A1AA] max-w-xl mx-auto leading-relaxed">
            Your appointment request has been sent for approval. Once approved, you will receive a confirmation message on WhatsApp.
          </p>

          {error && <p className="mt-10 text-red-400">{error}</p>}
          {appt && (
            <div className="mt-14 border border-[rgba(184,147,90,0.25)] p-8 text-left bg-[#0E0E0E]" data-testid="confirmation-card">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow">Reference</p>
                  <p className="serif text-2xl mt-2" data-testid="confirmation-reference">{appt.reference}</p>
                </div>
                <span className="text-xs tracking-[0.15em] uppercase border border-[#B8935A] text-[#B8935A] px-4 py-2" data-testid="confirmation-status">
                  Pending Approval
                </span>
              </div>
              <div className="gold-line my-6" />
              <div className="grid sm:grid-cols-2 gap-6 text-sm">
                <div><p className="eyebrow">Customer</p><p className="mt-2 text-base">{appt.customer_name}</p></div>
                <div><p className="eyebrow">Mobile</p><p className="mt-2 text-base">{appt.mobile}</p></div>
                <div className="sm:col-span-2"><p className="eyebrow">Services</p><p className="mt-2 text-base leading-relaxed">{appt.services.join(", ")}</p></div>
                <div><p className="eyebrow">Date</p><p className="mt-2 text-base">{appt.appointment_date}</p></div>
                <div><p className="eyebrow">Time</p><p className="mt-2 text-base">{fmt12(appt.appointment_time)}</p></div>
                {appt.note && <div className="sm:col-span-2"><p className="eyebrow">Note</p><p className="mt-2 text-base leading-relaxed">{appt.note}</p></div>}
              </div>
            </div>
          )}

          {appt && ownerWa && (
            <div className="mt-10 border border-[#B8935A] bg-[#B8935A]/8 p-6 text-left" data-testid="owner-notify-block">
              <p className="eyebrow">Almost done</p>
              <p className="serif text-xl md:text-2xl mt-3">Tap below to send your request to the studio on WhatsApp.</p>
              <p className="mt-2 text-sm text-[#A1A1AA]">This helps NOIR NAILS confirm your slot faster. If your browser blocked the new tab, use this link.</p>
              <a href={ownerWa} target="_blank" rel="noopener noreferrer" data-testid="owner-notify-link" className="btn-gold mt-5 inline-flex">
                <MessageCircle size={14} /> Notify Studio on WhatsApp
              </a>
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:justify-center">
            <Link to="/" data-testid="conf-back-home" className="btn-outline"><ArrowLeft size={14} /> Back to Home</Link>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="conf-contact" className="btn-primary">
              <MessageCircle size={14} /> Contact NOIR NAILS
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
